import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { CheckCircle } from 'lucide-react';
import '../styles/UserDashboard.css';
import '../styles/Home.css';
import '../styles/PaymentForm.css';

// Initialize Stripe once
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function safeJsonParse(text) {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

function PaymentForm({ token, email, name }) {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [amount, setAmount] = useState(1000);
  const [currency, setCurrency] = useState('huf');

  // This component is no longer used in the new flow
  // Payment session creation is handled by the main PaymentPage component

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError(null);
    if (!stripe || !elements) {
      setError("Stripe nem töltődött be");
      setProcessing(false);
      return;
    }
    const cardElement = elements.getElement(CardElement);
    const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
        },
      }
    );
    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
      return;
    }
    try {
      // This code is no longer used - payment handled via redirect
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container-small" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div className="loading-spinner" style={{marginRight: '10px'}} />
        Fizetési adatok betöltése...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="api-error" style={{
        padding: '16px',
        background: '#fff',
        border: '1px solid #ffcdd2',
        borderRadius: '8px',
        color: '#d32f2f',
        margin: '20px 0'
      }}>
        <strong>Hiba:</strong> {error}
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="purchased-package" style={{
        textAlign: 'center',
        padding: '30px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{fontSize: '48px', marginBottom: '20px'}}>🎉</div>
        <h3 style={{color: '#049A63', marginBottom: '10px', fontSize: '24px', fontWeight: '600'}}>Sikeres fizetés!</h3>
        <div style={{color: '#666', fontSize: '16px'}}>Az igazolást és a QR kódot elküldtük e-mailben.</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="payment-form" style={{padding: '20px'}}>
      <div className="form-row">
        <label htmlFor="card-element" style={{
          display: 'block',
          marginBottom: '12px',
          color: '#32325d',
          fontSize: '16px',
          fontWeight: '500'
        }}>
          Bankkártya adatok
        </label>
        <div style={{
          padding: '15px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          background: '#f8f9fa',
          transition: 'border-color 0.15s ease',
          '&:hover': {
            borderColor: '#049A63'
          }
        }}>
          <CardElement 
            id="card-element" 
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: '16px',
                  color: '#32325d',
                  fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                  padding: '10px 0',
                },
                invalid: {
                  color: '#fa755a',
                  iconColor: '#fa755a',
                },
              },
            }}
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
          color: 'white',
          backgroundColor: '#049A63',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          marginTop: '20px',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: '#038053'
          },
          '&:disabled': {
            backgroundColor: '#a0a0a0',
            cursor: 'not-allowed'
          }
        }}
      >
        {processing ? "Fizetés folyamatban..." : `Fizetés (${amount} Ft)`}
      </button>
    </form>
  );
}

const PaymentPage = () => {
  const [paymentContext, setPaymentContext] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Remove trailing slash to avoid double slashes in URLs
  const API_URL = (process.env.REACT_APP_API_URL || 'https://camino-backend-iooi.onrender.com').replace(/\/$/, '');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email") || params.get("userEmail");
    const nameParam = params.get("name") || params.get("userName");
    const lakeIdParam = params.get("lakeId");
    const leaderboardEntryIdParam = params.get("leaderboardEntryId");
    const userIdParam = params.get("userId") || params.get("userSub"); // Accept both userId and userSub
    const lakeNameParam = params.get("lakeName");
    
    console.log('💳 Payment page - URL params received:', {
      lakeId: lakeIdParam,
      leaderboardEntryId: leaderboardEntryIdParam,
      userId: userIdParam,
      userName: nameParam,
      userEmail: emailParam,
      lakeName: lakeNameParam
    });
    
    // Create payment context from URL parameters
    // Require lakeId and leaderboardEntryId (userId is optional for backward compatibility)
    if (lakeIdParam && leaderboardEntryIdParam) {
      const context = {
        lakeId: lakeIdParam,
        leaderboardEntryId: leaderboardEntryIdParam,
        userId: userIdParam,
        lakeName: lakeNameParam,
        userEmail: emailParam,
        userName: nameParam
      };
      
      console.log('✅ Payment context created:', context);
      setPaymentContext(context);
      localStorage.setItem('paymentContext', JSON.stringify(context));
      setLoading(false);
    } else {
      console.error('❌ Missing required parameters:', {
        hasLakeId: !!lakeIdParam,
        hasLeaderboardEntryId: !!leaderboardEntryIdParam
      });
      setError('Hiányzó fizetési információ az URL-ben.');
      setLoading(false);
    }
  }, []);

  // Handle payment session creation when paymentContext is set
  useEffect(() => {
    let mounted = true;
    
    async function createPaymentSession() {
      if (!paymentContext) {
        return; // Wait for payment context to be set
      }
      
      try {
        // Create Stripe checkout session using the new payment endpoint
        const res = await fetch(`${API_URL}/api/payments/create-stripe-session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            lakeId: paymentContext.lakeId,
            leaderboardEntryId: paymentContext.leaderboardEntryId,
            userId: paymentContext.userId,
            lakeName: paymentContext.lakeName,
            userEmail: paymentContext.userEmail,
            userName: paymentContext.userName
          }),
        });
        
        if (!mounted) return;
        
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Hiba a fizetési munkamenet létrehozásakor");
        
        if (data.url) {
          // Redirect to Stripe checkout
          window.location.href = data.url;
        } else {
          throw new Error('Nem érkezett fizetési URL');
        }
        
      } catch (err) {
        if (!mounted) return;
        console.error('Payment session creation error:', err);
        setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    createPaymentSession();
    return () => { mounted = false; };
  }, [paymentContext, API_URL]); // Trigger when paymentContext is set

  return (
    <div className="payment-section" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      padding: '40px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div className="payment-modal" style={{
        width: '100%',
        maxWidth: '500px',
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        overflow: 'hidden'
      }}>
        <div className="payment-modal-header" style={{
          background: '#049A63',
          padding: '20px',
          color: 'white',
          textAlign: 'center',
          position: 'relative'
        }}>
          <div style={{fontSize: '32px', marginBottom: '10px'}}></div>
          <h3 style={{margin: 0, fontSize: '24px', fontWeight: '600', color: 'white'}}>Ajándékcsomag</h3>
        </div>
        
        <div className="payment-modal-content" style={{padding: '24px'}}>
          <div className="payment-summary" style={{marginBottom: '30px'}}>
            <ul className="payment-features" style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 20px 0'
            }}>
              {[
                'Hivatalos Digitális Oklevél (PDF)',
                'QR Kód 2 adag ingyen fagylaltra',
                'Örök emlék a teljesítésről'
              ].map((feature, index) => (
                <li key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid #eee',
                  color: '#444'
                }}>
                  <CheckCircle size={20} color="#049A63" style={{marginRight: '12px', flexShrink: 0}} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <div className="payment-price" style={{
              textAlign: 'center',
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{color: '#666', marginBottom: '5px'}}>Fizetendő összeg:</div>
              <div style={{fontSize: '24px', color: '#049A63', fontWeight: '600'}}>1000 Ft</div>
            </div>
          </div>

          {error && (
            <div className="api-error" style={{
              padding: '12px',
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '8px',
              color: '#721c24',
              marginBottom: '15px'
            }}>
              {error}
            </div>
          )}
          
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              fontSize: '18px',
              color: '#666'
            }}>
              <div className="spinner" style={{
                width: '20px',
                height: '20px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #049A63',
                borderRadius: '50%',
                marginRight: '10px'
              }}></div>
              Átirányítás a fizetési oldalra...
            </div>
          ) : paymentContext ? (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              fontSize: '18px',
              color: '#049A63'
            }}>
              ✓ Fizetési információ rendben, átirányítás...
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;